import { PAYMENT_TYPE_PARAMS } from 'constants/global'
import { PAYMENT_TYPE } from 'constants/global'
import { PARAM_IS_EMBEDDED_VIEW } from 'constants/params'
import { PARAM_PAYMENT_METHOD } from 'constants/params'
import { PARAM_APIKEY } from 'constants/params'
import { PARAM_REFER_USER_ID } from 'constants/params'
import { PARAM_REFER_STATION_ID } from 'constants/params'
import { PARAM_IS_HEADER_MINI_APP } from 'constants/params'
import { PARAM_IS_TBPN_SELECT_VEHICLE_TYPE } from 'constants/params'
import { PARAM_HIDE_PAYMENT_METHOD } from 'constants/params'
import { LocalStorageManager } from 'helper/localStorage'
import { SessionStorageManager } from 'helper/localStorage'
import { smartParseParam } from 'helper/params'
import { getUrlParamValue } from 'helper/params'
import React from 'react'
import PropTypes from 'prop-types'
export const AppParamsContext = React.createContext(null)

const resolveParamsMiniAppSessionStorage = (config) => {
  // 1. param (raw)
  const param = getUrlParamValue(config.paramKey)

  // có param thì lưu session (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    SessionStorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const raw = param ?? SessionStorageManager.getItem(config.storageKey) ?? smartParseParam(process.env?.[config.envKey]) ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

const resolveParamsMiniAppLocalStorage = (config) => {
  // 1. param (raw)
  const param = getUrlParamValue(config.paramKey)

  // có param thì lưu local (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    LocalStorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const raw = param ?? LocalStorageManager.getItem(config.storageKey) ?? smartParseParam(process.env?.[config.envKey]) ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

/* =====================
 * PARAM MINI APP SCHEMA
 * ===================== */
const PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE = {
  apikey: {
    paramKey: PARAM_APIKEY,
    storageKey: PARAM_APIKEY,
    envKey: 'REACT_APP_APIKEY',
    defaultValue: undefined,
    parser: (v) => v
  },
  isEmbeddedView: {
    paramKey: PARAM_IS_EMBEDDED_VIEW,
    storageKey: PARAM_IS_EMBEDDED_VIEW,
    envKey: 'REACT_APP_PARAM_IS_EMBEDDED_VIEW',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  isHeaderMiniApp: {
    paramKey: PARAM_IS_HEADER_MINI_APP,
    storageKey: PARAM_IS_HEADER_MINI_APP,
    envKey: 'REACT_APP_PARAM_IS_HEADER_MINI_APP',
    defaultValue: undefined,
    parser: (v) => v
  },
  paymentMethodType: {
    paramKey: PARAM_PAYMENT_METHOD,
    storageKey: PARAM_PAYMENT_METHOD,
    envKey: 'REACT_APP_PAYMENT_METHOD',
    defaultValue: undefined,
    parser: (v) => {
      const paymentMethod = (v || '').toLowerCase()
      const key = Object.keys(PAYMENT_TYPE_PARAMS).find((k) => PAYMENT_TYPE_PARAMS[k].some((item) => item.toLowerCase() === paymentMethod))
      return key ? PAYMENT_TYPE[key] : undefined
    }
  },
  isTBPNSelectVehicleType: {
    paramKey: PARAM_IS_TBPN_SELECT_VEHICLE_TYPE,
    storageKey: PARAM_IS_TBPN_SELECT_VEHICLE_TYPE,
    envKey: 'REACT_APP_PARAM_IS_TBPN_SELECT_VEHICLE_TYPE',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  hidePaymentMethod: {
    paramKey: PARAM_HIDE_PAYMENT_METHOD,
    storageKey: PARAM_HIDE_PAYMENT_METHOD,
    envKey: 'REACT_APP_HIDE_PAYMENT_METHOD',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  }
}

const PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE = {
  referUserId: {
    paramKey: PARAM_REFER_USER_ID,
    storageKey: PARAM_REFER_USER_ID,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  },
  referStationId: {
    paramKey: PARAM_REFER_STATION_ID,
    storageKey: PARAM_REFER_STATION_ID,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  }
}

export const AppParamsContextProvider = (props) => {
  const paramsMiniAppSessionStorage = React.useMemo(() => {
    const params = {}

    Object.keys(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE).forEach((key) => {
      params[key] = resolveParamsMiniAppSessionStorage(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE[key])
    })

    return params
  }, [])

  const paramsMiniAppLocalStorage = React.useMemo(() => {
    const params = {}

    Object.keys(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE).forEach((key) => {
      params[key] = resolveParamsMiniAppLocalStorage(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE[key])
    })

    return params
  }, [])

  // pass resolved params through context value
  return React.createElement(
    AppParamsContext.Provider,
    {
      value: {
        ...paramsMiniAppSessionStorage,
        ...paramsMiniAppLocalStorage
      }
    },
    props.children
  )
}
export const useAppParamsContext = () => {
  const context = React.useContext(AppParamsContext)
  if (!context) {
    throw new Error('useAppParamsContext must be used within AppParamsContextProvider')
  }
  return context
}

AppParamsContextProvider.propTypes = {
  children: PropTypes.node
}
